'use client';
import React, { useState, useEffect, useRef } from 'react';
import AppLogo from '@/components/ui/AppLogo';

const navLinks = [
  { label: 'Curriculum', href: '#curriculum' },
  { label: 'Tools', href: '#tools' },
  { label: 'Certification', href: '#certification' },
  { label: 'Pricing', href: '#pricing' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const sections = ['curriculum', 'tools', 'certification', 'pricing'];
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-40% 0px -40% 0px' }
    );
    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observerRef.current?.observe(el);
    });
    return () => observerRef.current?.disconnect();
  }, []);

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* Anchor Nav — pins after hero */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-slate-deep/90 backdrop-blur-xl border-b border-frost/5 shadow-[0_4px_32px_rgba(0,0,0,0.4)]'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between gap-6">
          {/* Logo */}
          <a
            href="#"
            className="flex items-center gap-3 shrink-0"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          >
            <AppLogo
              size={36}
              text="Academy"
              iconName="AcademicCapIcon"
              className="text-sky-DEFAULT"
            />
          </a>

          {/* Desktop Anchor Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNavClick(link.href)}
                className={`nav-link text-sm font-medium tracking-wide pb-1 transition-colors duration-200 ${
                  activeSection === link.href.replace('#', '')
                    ? 'text-frost active' :'text-chrome-dim hover:text-frost'
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* CTA */}
          <div className="flex items-center gap-3">
            <a
              href="#download"
              className="btn-primary hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-slate-deep tracking-wide"
              onClick={(e) => { e.preventDefault(); document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' }); }}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 2a8 8 0 100 16A8 8 0 0010 2zm0 12l-4-4h2.5V8h3v2H14l-4 4z"/>
              </svg>
              Download Free
            </a>

            {/* Mobile menu toggle */}
            <button
              className="md:hidden text-chrome-dim hover:text-frost transition-colors p-2"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                {menuOpen
                  ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
                  : <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16"/>
                }
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden glass-card border-t border-frost/5">
            <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="text-left px-4 py-3 rounded-lg text-sm font-medium text-chrome hover:text-frost hover:bg-frost/5 transition-all"
                >
                  {link.label}
                </button>
              ))}
              <a
                href="#download"
                className="btn-primary mt-2 flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-slate-deep"
                onClick={(e) => { e.preventDefault(); setMenuOpen(false); document.getElementById('download')?.scrollIntoView({ behavior: 'smooth' }); }}
              >
                Download & Start Free
              </a>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}